// Alpine controller for the property-level "other_properties" CMS widget: a
// reorderable list of the site's OTHER properties, each with an eye toggle to
// show/hide it from THIS property's "explore more stays" section.
//
// No per-row modal (unlike cmsPropertyCards) — drag reorders, the eye flips
// `enabled`, and both immediately re-emit the full ordered list to
// property:sections.other_properties (the generic `property:sections.<section>`
// editor route — editor.ts:594 — which deep-merges the payload into
// content/properties/{locale}/{slug}.json#sections.other_properties). Mirrors
// cmsPropertyCards' Sortable init-in-$nextTick + applySortableReorder pattern.

import { createReorderable, applySortableReorder } from "../shared/sortable-reorder";
import { parseJsonOr } from "../shared/utils";
import type Sortable from "sortablejs";

interface Row {
  slug: string;
  name: string;
  enabled: boolean;
  // Hidden mirrors WebsiteProperty.Hidden (Task 8): the sibling's dedicated
  // page is hidden site-wide. Read-only here — visibility toggles exclusively
  // in the sidebar (Task 14). Drives a dimmed row + disabled enable toggle.
  hidden: boolean;
}

export function cmsOtherProperties(config: { field: string; items: string }) {
  return {
    field: config.field,
    items: parseJsonOr(config.items, [] as Row[]),
    _sortable: null as Sortable | null,
    init() {
      (this as any).$nextTick(() => {
        const el = (this as any).$refs.itemList as HTMLElement | undefined;
        if (!el) return;
        this._sortable = createReorderable(el, {
          handle: "[data-op-handle]",
          draggable: "[data-op]",
          onEnd: (e: Sortable.SortableEvent) => {
            this.items = applySortableReorder(el, e, this.items, "data-op-slug");
            this._emit();
          },
        });
      });
    },
    destroy() {
      this._sortable?.destroy();
      this._sortable = null;
    },
    toggle(slug: string) {
      const it = this.items.find((r) => r.slug === slug);
      // The templ disables the button while hidden; this guard is
      // defense-in-depth so a bypassed/stale DOM can never flip `enabled` for
      // a globally-hidden accommodation.
      if (!it || it.hidden) return;
      it.enabled = !it.enabled;
      this._emit();
    },
    _emit() {
      ((this as any).$root as HTMLElement).dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: {
            field: "other_property_items",
            target: "property:sections.other_properties",
            value: this.items.map((r) => ({ slug: r.slug, enabled: r.enabled })),
          },
          bubbles: true,
        }),
      );
    },
  };
}
